import React, { useEffect, useState, useContext } from "react";
import {
  Menu,
  MenuItem,
  Avatar,
  Box,
  Divider,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  Person as PersonIcon,
  PersonOutline as ProfileIcon,
  GridViewOutlined as ProjectsIcon,
  CreditCardOutlined as BillingIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../auth/Auth";
import { useLoginState } from "./LoginState";
import { SignIn } from "./SignIn";
import { SignUp } from "./SignUp";
import { Forgot } from "./Forgot";
import AuthModal from "./AuthModal";
import ProfileModal from "./ProfileModal";
import { PersonIconButton } from "./styles";
import { MyProfilePic } from "./MyProfilePic";
import { Reset } from "./Reset";
import {useMattoState} from '../../MattoState'
import { WorkLogin } from "./WorkLogin";
import { VerificationScreen } from "./VerificationScreen";
import VerificationFailed from "./VerificationFailed";
import { signOutAndGo } from "./signOutAndGo";
import { useTeamWelcomeStore } from "../../team/onboarding/welcomeGate";
import { setSentryUser, clearSentryUser } from '../../libs/sentry';

const MENU_PAPER_SX = {
  width: 320,
  maxWidth: "calc(100vw - 24px)",
  maxHeight: "calc(100vh - 96px)",
  marginTop: "8px",
  overflowX: "hidden",
  overflowY: "auto",
  borderRadius: "16px",
  border: "1px solid rgba(0, 0, 0, 0.06)",
  boxShadow: "0 24px 48px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0, 0, 0, 0.08)",
};

// The account email above the items. MenuList skips it for initial focus
// (otherwise the arrow keys are dead when the menu opens).
const EmailCaption = ({ children }) => (
  <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      noWrap
      sx={{ display: "block" }}
    >
      {children}
    </Typography>
  </Box>
);
EmailCaption.muiSkipListHighlight = true;

// Shared row styling for the personal-account items so this dropdown matches
// the redesigned Notifications / Help menus: a circular gray icon swatch with
// a bold primary label and a muted secondary description.
const ACCOUNT_ITEM_SX = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  padding: "8px 16px",
  borderRadius: "12px",
  whiteSpace: "normal",
  "&:hover": { backgroundColor: "#f7f7f7" },
};

function AccountMenuItem({ icon: Icon, primary, secondary, onClick, ...rest }) {
  return (
    <MenuItem onClick={onClick} sx={ACCOUNT_ITEM_SX} {...rest}>
      <Avatar
        sx={{
          backgroundColor: "#f1f1f3",
          color: "#71717A",
          width: 48,
          height: 48,
          flexShrink: 0,
          "& .MuiSvgIcon-root": { fontSize: "20px" },
        }}
      >
        <Icon />
      </Avatar>
      <ListItemText
        primary={primary}
        secondary={secondary}
        primaryTypographyProps={{ sx: { fontSize: "14px", fontWeight: 600, color: "#1a1a1a" } }}
        secondaryTypographyProps={{ sx: { fontSize: "12px", color: "#888" } }}
      />
    </MenuItem>
  );
}

export const LoginModal = () => {
  const setShowLogin = useLoginState((state) => state.setShowLogin);
  const showLogin = useLoginState((state) => state.showLogin);
  const handleClose = () => {
    setShowLogin(null);
  }


  if (showLogin == "signin") {
    return (
      <AuthModal open={true} onClose={handleClose} graphic="signin">
        <SignIn />
      </AuthModal>
    );
  } else if (showLogin == "signup") {
    return (
      <AuthModal open={true} onClose={handleClose} graphic="signup">
        <>
          {" "}
          <SignUp  />{" "}
        </>
      </AuthModal>
    );
  } else if (showLogin == "forgot") {
    return (
      <AuthModal open={true} onClose={handleClose} graphic="signin">
        <>
          {" "}
          <Forgot />{" "}
        </>
      </AuthModal>
    );
  }  else if (showLogin == "reset") {
    return (
      <AuthModal open={true} onClose={handleClose} graphic="signin">
        <>
          {" "}
          <Reset />{" "}
        </>
      </AuthModal>
    );
  } else if (showLogin == "work_login") {
    return (
      <AuthModal open={true} onClose={handleClose} graphic="signin">
        <>
          {" "}
          <WorkLogin />{" "}
        </>
      </AuthModal>
    );
  } else if (showLogin == "work_login_success") {
    return (
      <AuthModal open={true} onClose={handleClose} graphic="none">
        <>
          {" "}
          <SignUp activeStep={1} />{" "}
        </>
      </AuthModal>
    );
  } else if (showLogin == "verification_screen") {
    return (
      <AuthModal open={true} onClose={handleClose} graphic="none">
        <>
          {" "}
          <VerificationScreen />{" "}
        </>
      </AuthModal>
    );
  } else if (showLogin == "verification_failed") {
    return (
      <AuthModal open={true} onClose={handleClose} graphic="none">
        <>
          {" "}
          <VerificationFailed />{" "}
        </>
      </AuthModal>
    );
  } else return null;
};

export default function AuthButton() {
  const setShowLogin = useLoginState((state) => state.setShowLogin);
  const showLogin = useLoginState((state) => state.showLogin);

  const { currentUser }  = useContext(AuthContext);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [open, setOpen] = useState(false);
  const profile = useLoginState((state) => state.profile);
  const setProfile = useLoginState((state) => state.setProfile);
  const getUserProfile = useLoginState((state) => state.getUserProfile);
  const [profileModalShown, setProfileModalShown] = useState(false);
	const setShowSubscription = useMattoState((state) => state.setShowSubscription);
  const setOrCreateProfile = useLoginState((state) => state.setOrCreateProfile);
  const [attemptedProfile, setAttemptedProfile] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) {
      // Only clear profile on logout, not when user is opening login/signup modal
      if (!showLogin) {
        setProfile({});
      }
      return;
    }
    // Don't interfere with signup flow - SignupForm.handleProfileCreation handles profile creation
    if (showLogin === 'signup') return;
    loadProfile(); },
  [currentUser?.uid, showLogin]);

  // Deep-link: ?modal=profile opens the profile form (the SketchUp panel's
  // "Finish your profile" — panel sign-ups never see the signup stepper's
  // onboarding step). Bar.tsx owns the other ?modal= values.
  useEffect(() => {
    if (!profile?.id) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('modal') !== 'profile') return;
    setProfileModalShown(true);
    params.delete('modal');
    const search = params.toString();
    navigate(
      { pathname: window.location.pathname, search: search ? `?${search}` : '' },
      { replace: true },
    );
  }, [profile?.id, navigate]);

  useEffect(() => {
    if (attemptedProfile==false) return;
    setTimeout(() => {
      if (!currentUser?.uid) { setProfile({}); return; }
      if (useLoginState.getState().profile?.uid) return;
      const data = { user: { uid: currentUser?.uid,email: currentUser?.email,} };
      console.log('setOrCreateProfile called from Auth for user:', data.user.email);
      setOrCreateProfile(data);
    },1500)
  },[attemptedProfile])

  const loadProfile = (force=false) =>{
      if (!currentUser?.uid) { setProfile({}); return; }
      const existingProfile = useLoginState.getState().profile;
      // Only skip fetch if existing profile belongs to current user
      if (existingProfile?.uid && existingProfile.uid === currentUser.uid && force!=true) {
        // Profile already exists for current user (e.g., from signup flow), just set Sentry context
        try {
          setSentryUser(existingProfile);
        } catch (error) {
          console.error('Error setting Sentry context', error);
        }
        setAttemptedProfile(true);
        return;
      }
      const data = { user: { uid: currentUser?.uid,email: currentUser?.email,} };
      getUserProfile(data).then((profile) => {
        // Set additional Sentry context when profile is loaded
        try {
          if (profile) {
            setSentryUser(profile);
          }
        } catch (error) {
          console.error('Error setting Sentry context', error);
        }
        setAttemptedProfile(true);
      })
  }
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };
  const goToProfile = () => {
    handleClose();
    setProfileModalShown(true);
  };
  const goToProjects = () => { handleClose(); window.location.href = "/home/projects"; }

  const cancelProSubscription = () => { handleClose(); setShowSubscription('cancel') }
  const goToAccounts = () => {
    handleClose(); setShowSubscription('accounts')
  }
  // The menu stays open while pending saves finish (up to 8 s); a second
  // click must not sign out twice.
  const handleLogout = () => {
    if (signingOut) return;
    setSigningOut(true);
    signOutAndGo("/", () => {
      setShowLogin(null);
      handleClose();
    });
  };
  const handleProfileModalSuccess = () => { loadProfile(true); }

  // The one-time team welcome waits while the profile form is open.
  useEffect(() => {
    useTeamWelcomeStore.getState().setBlocker("profile", profileModalShown);
    return () => useTeamWelcomeStore.getState().setBlocker("profile", false);
  }, [profileModalShown]);

  // Workspaces are switched next to the logo (WorkspaceSwitcher); this menu
  // holds only the personal-account items.
  return (
    <>
      {currentUser ? (
        <div>
          <PersonIconButton
            id="basic-button"
            edge="start"
            onClick={handleClick}
            aria-label="account of current user"
            aria-haspopup="true"
            aria-controls={open ? "basic-menu" : undefined}
            aria-expanded={open ? "true" : undefined}
            data-testid="account-menu-button"
          >
            <MyProfilePic img={profile?.files?.pic} />
          </PersonIconButton>

          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
              sx: { padding: "8px" },
            }}
            slotProps={{ paper: { sx: MENU_PAPER_SX } }}
          >
            {currentUser?.email && (
              <EmailCaption>{currentUser.email}</EmailCaption>
            )}

            <AccountMenuItem
              icon={ProfileIcon}
              primary="Your Profile"
              secondary="View and manage your profile"
              onClick={goToProfile}
            />
            <AccountMenuItem
              icon={ProjectsIcon}
              primary="Your Projects"
              secondary="View your projects and boards"
              onClick={goToProjects}
            />
            <AccountMenuItem
              icon={BillingIcon}
              primary="Accounts & Billing"
              secondary="Manage your subscription and billing"
              onClick={goToAccounts}
            />

            {/* {profile?.userRole == UserProfileRole.PRO &&
              <MenuItem onClick={cancelProSubscription}>Cancel Mattoboard Pro</MenuItem>
            } */}
            {/* {useStaging &&
              <MenuItem onClick={switchSubscription}>{profile?.userRole == UserProfileRole.PRO ? 'Switch to Non-Pro' : 'Switch to Pro' }</MenuItem>
            } */}
            <Divider sx={{ my: 0.5, mx: "-8px" }} />
            <AccountMenuItem
              icon={LogoutIcon}
              primary={signingOut ? "Signing out…" : "Logout"}
              secondary="Sign out of your account"
              onClick={handleLogout}
              disabled={signingOut}
            />
          </Menu>

          {profileModalShown && (
          <ProfileModal
              open={profileModalShown}
              initialProfile={profile}
              onClose={() => setProfileModalShown(false)}
              onSuccess={handleProfileModalSuccess}
            />
          )}
        </div>
      ) : (
        <>
          <PersonIconButton edge="start" onClick={() => setShowLogin("signup")}>
            <PersonIcon />
          </PersonIconButton>
        </>
      )}
      <LoginModal />
    </>
  );
}
